import { isPlatformServer } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';

import { WINDOW } from '../browser/window/window.token';
import { LanguageInfo, Page } from '../client-config/page.client-config';
import { isAbsolute } from '../utils/url';
import { AppendReferrerOptions, NavigationUrl } from './navigation.models';
import { ParsedUrl } from './parsed-url';
import { QuerySearchParams } from './query-search-params';

/**
 * @whatItDoes Parses url into parts, then puts it back together (parts can be modified).
 *
 * @howToUse
 *
 * ```
 * var someUrl = 'http://www.google.com/search?a=1';
 * var url = urlService.parse(someUrl);
 *
 * url.set('param', 'hello');
 *
 * url.url() // returns '/search?a=1&param=hello'
 * url.absUrl() // http://www.google.com/search?a=1&param=hello
 * ```
 *
 * ```
 * var someUrl = '/search?a=1';
 * var url = urlService.parse(someUrl);
 *
 * url.set('param', 'hello');
 *
 * url.url() // returns '/search?a=1&param=hello'
 * url.absUrl() // http://www.google.com/search?a=1&param=hello
 * ```
 *
 * @description
 *
 * A helper service used for parsing urls, and making complex changes to them. Url is parsed using virtual `a` DOM node.
 *
 * See {@link ParsedUrl}
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class UrlService {
    private readonly page = inject(Page);

    readonly #window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);

    /**
     * @internal
     */
    readonly supportedLanguages: string[];

    /**
     * @internal
     */
    readonly culturePattern: string;

    private readonly parsingAnchorElement: HTMLAnchorElement | null = isPlatformServer(this.platformId) ? null : this._doc.createElement('a');

    private parsingNode(interpretedUrl: NavigationUrl): HTMLAnchorElement | URL {
        let href = interpretedUrl.href;
        if (!this.parsingAnchorElement) {
            if (interpretedUrl.isRelative) {
                href = `${this.#window.location.protocol}//${this.#window.location.hostname}${href}`;
            }
            return new URL(href);
        }
        if ((<any>this._doc).documentMode) {
            this.parsingAnchorElement.setAttribute('href', href);

            href = this.parsingAnchorElement.href;
        }

        this.parsingAnchorElement.setAttribute('href', href);
        return this.parsingAnchorElement;
    }

    constructor() {
        this.supportedLanguages = this.page.uiLanguages.concat(this.page.hiddenLanguages).map((l: LanguageInfo) => l.routeValue);
        this.culturePattern = '^/?(' + this.supportedLanguages.join('|') + ')(/|\\?|\\(|$)';
    }

    /**
     * Parses specified url and returns {@link ParsedUrl} instance.
     */
    parse(url: string): ParsedUrl {
        const interpretedUrl = this.interpretUrl(url);
        const parsingNode = this.parsingNode(interpretedUrl);

        const searchParams = new QuerySearchParams(parsingNode.search ? parsingNode.search.replace(/^\?/, '') : '');
        const protocol = parsingNode.protocol ? parsingNode.protocol.replace(/:$/, '') : '';
        const hash = parsingNode.hash ? parsingNode.hash.replace(/^#/, '') : '';
        const hostname = parsingNode.hostname;
        const port = parsingNode.port;
        const pathname =
            parsingNode.pathname.startsWith('/') && parsingNode.pathname.charAt(1) !== '/' ? parsingNode.pathname : '/' + parsingNode.pathname;
        // bwin custom
        const isSameHost = this.#window.location.href.startsWith(parsingNode.protocol + '//' + parsingNode.host);
        const isSameTopDomain = parsingNode.hostname.endsWith(this.page.domain);
        const isRelative = interpretedUrl.isRelative;
        const culture = this.getCulture(pathname);

        return new ParsedUrl(this, protocol, searchParams, hash, hostname, port, pathname, isRelative, isSameHost, isSameTopDomain, culture);
    }

    /**
     * Parses current browser url and returns {@link ParsedUrl} instance.
     */
    current(): ParsedUrl {
        return this.parse(this.#window.location.href);
    }

    /**
     * Returns true if specified url is absolute. Otherwise, returns false.
     */
    isAbsolute(url: string): boolean {
        return isAbsolute(url);
    }

    /**
     * Adds a `rurl` query string parameter to the url. See {@link ParsedUrl#appendReferrer ParsedUrl.appendReferrer()}.
     */
    appendReferrer(url: string, options?: AppendReferrerOptions): string {
        const resultUrl = this.parse(url);

        resultUrl.appendReferrer(options);

        return resultUrl.absUrl();
    }

    private getCulture(path: string): string | null {
        // if there is no language in the current location it means the default language is active
        if (path === '' || path === '/') {
            return this.page.defaultLanguage.routeValue;
        }

        const cultureRegex = new RegExp(this.culturePattern, 'gi');

        const culture = cultureRegex.exec(path);

        // problem getting the language code
        if (culture == null) {
            return null;
        }

        // return languageCode from pathname
        return culture[1] ?? null;
    }

    private interpretUrl(url: string): NavigationUrl {
        url = this.replaceUrlPlaceholders(url);

        if (this.isAbsolute(url)) {
            return { href: url, isRelative: false };
        }

        if (url === '/') {
            return { href: '/' + this.page.lang, isRelative: true };
        }

        if (url.startsWith('/')) {
            const cultureRegex = new RegExp(this.culturePattern, 'gi');
            if (cultureRegex.test(url)) {
                // url already begins at root and includes language prefix
                return { href: url, isRelative: true };
            }

            return { href: '/' + this.page.lang + url, isRelative: true };
        }

        if (url.startsWith('?')) {
            return { href: this.#window.location.pathname + url, isRelative: true };
        }

        if (url.startsWith('htcmd')) {
            return { href: url, isRelative: true };
        }
        return { href: this.#window.location.pathname + '/' + url, isRelative: true };
    }

    private replaceUrlPlaceholders(url: string): string {
        return url?.replace(/{culture}/g, this.page.lang) || url;
    }
}
